import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonAvatar,
  IonBadge,
  IonFab,
  IonFabButton,
  IonIcon,
  IonNote,
  IonButtons,
  IonMenuButton,
  IonSpinner,
  IonRefresher,
  IonRefresherContent,
  IonSelect,
  IonSelectOption,
  useIonRouter,
} from '@ionic/react';
import {
  add,
  peopleOutline,
  search,
  chevronBack,
  chevronForward,
  call,
  locationOutline,
} from 'ionicons/icons';
import { useFamilies } from '@/hooks/useFamilies';
import { useSectors } from '@/hooks/useSectors';
import { usePermission } from '@/hooks/usePermission';
import FilterBar, { type FilterGroup } from '@/components/shared/FilterBar';
import type { FamilyFilters, Family, FamilyMember } from '@/types/family';
import { FAMILY_ROLE_LABELS } from '@/utils/labels';

const ROLE_ORDER: Record<string, number> = { head: 0, spouse: 1, child: 2 };
const ROLE_DOT_COLORS: Record<string, string> = { head: '#5e81f4', spouse: '#9698d6', child: '#7ce7ac' };

/* ── Family card view for search results ── */
const FamilyCardResults: React.FC<{
  families: Family[];
  onFamilyClick: (id: string) => void;
  onMemberClick: (id: string) => void;
}> = ({ families, onFamilyClick, onMemberClick }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    {families.map((family) => {
      const sortedMembers = [...(family.members ?? [])]
        .sort((a, b) => (ROLE_ORDER[a.family_role ?? ''] ?? 9) - (ROLE_ORDER[b.family_role ?? ''] ?? 9));
      const address = [family.address, family.city].filter(Boolean).join(', ');
      return (
        <div key={family.id} style={{
          background: '#fff', borderRadius: 14, border: '1px solid #ececf2', overflow: 'hidden',
        }}>
          {/* Family header */}
          <div
            onClick={() => onFamilyClick(family.id)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #ececf2',
              cursor: 'pointer', flexWrap: 'wrap', gap: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 38, height: 38, borderRadius: 12, flexShrink: 0,
                background: '#5e81f4', color: '#fff', fontSize: 16, fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {(family.name[0] ?? '').toUpperCase()}
              </div>
              <div>
                <span style={{ fontWeight: 700, fontSize: 15, color: '#1c1d21' }}>
                  Familia {family.display_name}
                </span>
                <span style={{ fontSize: 12, color: '#8181a5', marginLeft: 8 }}>
                  ({family.member_count} {family.member_count === 1 ? 'persoană' : 'persoane'})
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {family.sector_name && <span className="crm-badge crm-badge--sector">{family.sector_name}</span>}
              <span style={{ fontSize: 12, color: '#5e81f4', fontWeight: 600 }}>Detalii →</span>
            </div>
          </div>

          {/* Address */}
          {address && (
            <div style={{ padding: '8px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 6 }}>
              <IonIcon icon={locationOutline} style={{ fontSize: 14, color: '#8181a5', flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#64748b' }}>{address}</span>
            </div>
          )}

          {/* Members */}
          <div style={{ padding: '4px 0' }}>
            {sortedMembers.length === 0 ? (
              <div style={{ padding: '12px 20px', fontSize: 12, color: '#94a3b8' }}>Niciun membru.</div>
            ) : sortedMembers.map((m: FamilyMember) => {
              const roleColor = ROLE_DOT_COLORS[m.family_role ?? ''] ?? '#94a3b8';
              return (
                <div
                  key={m.id}
                  onClick={(e) => { e.stopPropagation(); onMemberClick(m.id); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 20px', cursor: 'pointer', transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    background: roleColor, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                  }}>
                    {(m.first_name?.[0] ?? '?').toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 600, fontSize: 13, color: '#1c1d21' }}>
                        {m.full_name ?? m.first_name ?? '—'}
                      </span>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                        color: roleColor, background: `${roleColor}15`,
                      }}>
                        {FAMILY_ROLE_LABELS[m.family_role ?? ''] ?? 'Membru'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#8181a5', marginTop: 1 }}>
                      {m.age != null ? `${m.age} ani` : ''}
                      {m.church_status && m.church_status !== 'active' ? ` · ${m.church_status}` : ''}
                    </div>
                  </div>
                  {m.cell_phone && (
                    <a href={`tel:${m.cell_phone}`} onClick={(e) => e.stopPropagation()}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#5e81f4', textDecoration: 'none', flexShrink: 0 }}>
                      <IonIcon icon={call} style={{ fontSize: 13 }} />{m.cell_phone}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    })}
  </div>
);

/* ── Sortable column definitions ── */
type SortKey = 'name' | 'address' | 'sector_name' | 'member_count';

interface ColumnDef {
  key: SortKey;
  label: string;
  minWidth?: number;
}

const COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Nume Familie', minWidth: 200 },
  { key: 'address', label: 'Adresă', minWidth: 180 },
  { key: 'sector_name', label: 'Sector', minWidth: 120 },
  { key: 'member_count', label: 'Membri', minWidth: 80 },
];

const PAGE_SIZES = [25, 50, 100] as const;

/* ── Responsive hook ── */
function useIsMobile(breakpoint = 992) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  );

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, [breakpoint]);

  return isMobile;
}

/* ══════════════════════════════════════════
   FamilyListPage
   ══════════════════════════════════════════ */
const FamilyListPage: React.FC = () => {
  const router = useIonRouter();
  const { can } = usePermission();
  const isAdmin = can('families.create');
  const isMobile = useIsMobile();

  /* ── Local state ── */
  const [searchText, setSearchText] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [filterUpdatedWithin, setFilterUpdatedWithin] = useState<'' | '7' | '30' | '90'>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [showSearch, setShowSearch] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── Sector filter chips (dynamic) ── */
  const { data: sectors } = useSectors();
  const filterGroups: FilterGroup[] = useMemo(() => {
    if (!sectors || sectors.length === 0) return [];
    return [
      {
        name: 'Sector',
        options: sectors.map((s) => ({
          key: 'sector_id',
          label: s.name,
          value: s.id,
          color: 'primary',
        })),
      },
    ];
  }, [sectors]);

  /* ── Build API filters ── */
  const filters: FamilyFilters = {
    search: searchText || undefined,
    sector_id: activeFilters['sector_id'] || undefined,
    updated_within: filterUpdatedWithin ? Number(filterUpdatedWithin) : undefined,
    sort_by: sortKey,
    sort_dir: sortAsc ? 'asc' : 'desc',
    page: currentPage,
    page_size: pageSize,
  };

  const { data, isLoading, isFetching, refetch } = useFamilies(filters);
  const families: Family[] = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  /* ── Handlers ── */
  const handleSearch = useCallback((value: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setSearchText(value);
      setCurrentPage(1);
    }, 300);
  }, []);

  const handleFilterChange = useCallback((key: string, value: string | undefined) => {
    setActiveFilters((prev) => {
      const next = { ...prev };
      if (value === undefined) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
    setCurrentPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setActiveFilters({});
    setFilterUpdatedWithin('');
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortAsc((prev) => !prev);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
    setCurrentPage(1);
  }, [sortKey]);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleRefresh = async (event: CustomEvent) => {
    await refetch();
    event.detail.complete();
  };

  const navigateToFamily = useCallback((id: string) => {
    router.push(`/families/${id}`);
  }, [router]);

  /* ── Sort arrow indicator ── */
  const sortArrow = (key: SortKey) => (sortKey === key ? (sortAsc ? ' ↑' : ' ↓') : '');

  /* ── Pagination page numbers (smart windowing) ── */
  const pageNumbers = useMemo(() => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('ellipsis');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  }, [totalPages, currentPage]);

  /* ══════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════ */
  return (
    <IonPage>
      {/* ── Header ── */}
      <IonHeader>
        <IonToolbar className="crm-toolbar">
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Familii</IonTitle>
          <IonButtons slot="end">
            <button
              className="crm-toolbar-icon"
              onClick={() => setShowSearch((s) => !s)}
              aria-label="Căutare"
            >
              <IonIcon icon={search} />
            </button>
            <IonLabel
              style={{
                fontSize: '13px',
                padding: '0 16px',
                color: 'var(--ion-color-medium)',
              }}
            >
              {totalCount} total
            </IonLabel>
          </IonButtons>
        </IonToolbar>

      </IonHeader>

      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div className="crm-page-layout">
          <div className="crm-main-content">
            {/* ── Inline search ── */}
            {showSearch && (
              <div style={{ marginBottom: 12 }}>
                <input
                  type="text"
                  placeholder="Caută familii..."
                  autoFocus
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 0', fontSize: 14, fontWeight: 700,
                    fontFamily: "'Lato', sans-serif", color: '#1c1d21',
                    border: 'none', borderBottom: '1px solid #ececf2', outline: 'none',
                    background: 'transparent',
                  }}
                  onFocus={(e) => { (e.target.style.borderBottomColor = '#5e81f4'); }}
                  onBlur={(e) => { (e.target.style.borderBottomColor = '#ececf2'); }}
                />
              </div>
            )}

            {/* ── Filter chips ── */}
            {filterGroups.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <FilterBar
                  groups={filterGroups}
                  activeFilters={activeFilters}
                  onFilterChange={handleFilterChange}
                  onClearAll={handleClearFilters}
                />
              </div>
            )}

            {/* ── Recently-updated quick filter ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8', marginRight: 4 }}>
                Actualizat
              </span>
              {([['', 'Oricând'], ['7', '7 zile'], ['30', '30 zile'], ['90', '90 zile']] as const).map(([val, label]) => {
                const active = filterUpdatedWithin === val;
                return (
                  <button
                    key={val || 'any'}
                    type="button"
                    onClick={() => { setFilterUpdatedWithin(val); setCurrentPage(1); }}
                    style={{
                      padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'inherit',
                      background: active ? '#4f46e5' : '#f1f5f9',
                      color: active ? '#fff' : '#475569',
                      border: `1px solid ${active ? '#4f46e5' : '#e2e8f0'}`,
                      transition: 'all .15s',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* ── Summary bar ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                {totalCount} {totalCount === 1 ? 'familie' : 'familii'} {(Object.keys(activeFilters).length > 0 || filterUpdatedWithin) ? '(filtrat)' : ''}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>Rânduri:</span>
                  <IonSelect
                    interface="popover"
                    value={pageSize}
                    onIonChange={(e) => handlePageSizeChange(e.detail.value as number)}
                    style={{ fontSize: 12, minWidth: 60, padding: 0 }}
                  >
                    {PAGE_SIZES.map((size) => (
                      <IonSelectOption key={size} value={size}>
                        {size}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </div>
              </div>
            </div>

            {/* ── Initial loading ── */}
            {isLoading && !data && (
              <div style={{ textAlign: 'center', padding: 60 }}>
                <IonSpinner name="crescent" />
                <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 12 }}>Se incarca...</p>
              </div>
            )}

            {/* ── Background fetch indicator ── */}
            {isFetching && data && (
              <div className="crm-fetch-indicator">
                <div className="crm-fetch-indicator__bar" />
              </div>
            )}

            {/* ── Family card view when searching ── */}
            {data && searchText && families.length > 0 && (
              <>
                <FamilyCardResults
                  families={families}
                  onFamilyClick={navigateToFamily}
                  onMemberClick={(id) => router.push(`/members/${id}`)}
                />
                {totalPages > 1 && (
                  <div className="crm-pagination" style={{ marginTop: 16 }}>
                    <button className="crm-pagination__btn" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
                      <IonIcon icon={chevronBack} style={{ fontSize: 12 }} /> PREV
                    </button>
                    {pageNumbers.map((p, idx) => p === 'ellipsis'
                      ? <span key={`e-${idx}`} style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>
                      : <button key={p} className={`crm-pagination__num ${p === currentPage ? 'crm-pagination__num--active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
                    )}
                    <button className="crm-pagination__btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
                      NEXT <IonIcon icon={chevronForward} style={{ fontSize: 12 }} />
                    </button>
                  </div>
                )}
                {families.length === 0 && (
                  <div className="crm-empty-state">
                    <div className="crm-empty-state__icon"><IonIcon icon={peopleOutline} /></div>
                    <h3 className="crm-empty-state__title">Nicio familie găsită</h3>
                    <p className="crm-empty-state__text">Încearcă să modifici căutarea sau filtrele aplicate.</p>
                  </div>
                )}
              </>
            )}

            {/* ── Desktop: Sortable data table (no search active) ── */}
            {data && !isMobile && !searchText && (
              <div className="crm-card" style={{ overflow: 'auto' }}>
                <table className="crm-table">
                  <thead>
                    <tr>
                      {COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          onClick={() => handleSort(col.key)}
                          style={{ minWidth: col.minWidth, cursor: 'pointer' }}
                        >
                          {col.label}{sortArrow(col.key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {families.map((family) => (
                      <tr
                        key={family.id}
                        onClick={() => navigateToFamily(family.id)}
                        style={{ cursor: 'pointer' }}
                        className="crm-animate-in"
                      >
                        {/* Nume */}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                backgroundColor: 'var(--ion-color-tertiary)',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 13,
                                fontWeight: 700,
                                flexShrink: 0,
                                marginTop: 2,
                              }}
                            >
                              {(family.name[0] ?? '').toUpperCase()}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div className="crm-table__name">{family.display_name}</div>
                              {family.members && family.members.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 8px', marginTop: 3 }}>
                                  {[...family.members]
                                    .sort((a, b) => (ROLE_ORDER[a.family_role ?? ''] ?? 9) - (ROLE_ORDER[b.family_role ?? ''] ?? 9))
                                    .map((m: FamilyMember) => (
                                      <span key={m.id} style={{ fontSize: 11, color: '#8181a5', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                        <span style={{
                                          width: 6, height: 6, borderRadius: '50%',
                                          background: ROLE_DOT_COLORS[m.family_role ?? ''] ?? '#94a3b8',
                                          flexShrink: 0,
                                        }} />
                                        {m.first_name || m.full_name?.split(' ')[0] || '?'}
                                      </span>
                                    ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {/* Adresă */}
                        <td>
                          {[family.address, family.city].filter(Boolean).join(', ') || (
                            <span style={{ fontSize: 12, color: '#cbd5e1' }}>-</span>
                          )}
                        </td>
                        {/* Sector */}
                        <td>
                          {family.sector_name ? (
                            <span className="crm-badge crm-badge--sector">{family.sector_name}</span>
                          ) : (
                            <span style={{ fontSize: 12, color: '#cbd5e1' }}>-</span>
                          )}
                        </td>
                        {/* Membri */}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <IonIcon icon={peopleOutline} color="medium" style={{ fontSize: 14 }} />
                            <span style={{ fontWeight: 600 }}>{family.member_count}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* ── Empty state (desktop) ── */}
                {families.length === 0 && (
                  <div className="crm-empty-state">
                    <div className="crm-empty-state__icon">
                      <IonIcon icon={peopleOutline} />
                    </div>
                    <h3 className="crm-empty-state__title">Nicio familie găsită</h3>
                    <p className="crm-empty-state__text">
                      Încearcă să modifici căutarea sau filtrele aplicate.
                    </p>
                  </div>
                )}

                {/* ── Pagination (desktop) ── */}
                {totalPages > 1 && (
                  <div className="crm-pagination">
                    <button
                      className="crm-pagination__btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                    >
                      <IonIcon icon={chevronBack} style={{ fontSize: 12 }} /> PREV
                    </button>
                    {pageNumbers.map((p, idx) =>
                      p === 'ellipsis' ? (
                        <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: '#94a3b8' }}>
                          ...
                        </span>
                      ) : (
                        <button
                          key={p}
                          className={`crm-pagination__num ${p === currentPage ? 'crm-pagination__num--active' : ''}`}
                          onClick={() => setCurrentPage(p)}
                        >
                          {p}
                        </button>
                      ),
                    )}
                    <button
                      className="crm-pagination__btn"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      NEXT <IonIcon icon={chevronForward} style={{ fontSize: 12 }} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── Mobile: Card layout (no search active) ── */}
            {data && isMobile && !searchText && (
              <>
                <IonList lines="full">
                  {families.map((family) => (
                    <IonItem
                      key={family.id}
                      button
                      detail
                      onClick={() => navigateToFamily(family.id)}
                    >
                      <IonAvatar
                        slot="start"
                        style={{ width: '44px', height: '44px' }}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            backgroundColor: 'var(--ion-color-tertiary)',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '18px',
                            fontWeight: 600,
                          }}
                        >
                          {(family.name[0] ?? '').toUpperCase()}
                        </div>
                      </IonAvatar>
                      <IonLabel>
                        <h2 style={{ fontWeight: 600 }}>{family.display_name}</h2>
                        <p style={{ fontSize: '13px' }}>
                          {[family.address, family.city].filter(Boolean).join(', ') || 'Fără adresă'}
                        </p>
                        {family.members && family.members.length > 0 && (
                          <p style={{ fontSize: 11, color: '#8181a5', margin: '2px 0 0' }}>
                            {[...family.members]
                              .sort((a, b) => (ROLE_ORDER[a.family_role ?? ''] ?? 9) - (ROLE_ORDER[b.family_role ?? ''] ?? 9))
                              .map((m: FamilyMember) => m.first_name || m.full_name?.split(' ')[0] || '?')
                              .join(', ')}
                          </p>
                        )}
                        {family.sector_name && (
                          <IonNote style={{ fontSize: '12px' }}>
                            {family.sector_name}
                          </IonNote>
                        )}
                      </IonLabel>
                      <div slot="end" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <IonIcon icon={peopleOutline} color="medium" />
                        <IonBadge color="medium" style={{ fontSize: '12px' }}>
                          {family.member_count}
                        </IonBadge>
                      </div>
                    </IonItem>
                  ))}
                </IonList>

                {/* ── Empty state (mobile) ── */}
                {families.length === 0 && (
                  <div className="crm-empty-state">
                    <div className="crm-empty-state__icon">
                      <IonIcon icon={peopleOutline} />
                    </div>
                    <h3 className="crm-empty-state__title">Nicio familie găsită</h3>
                    <p className="crm-empty-state__text">
                      Încearcă să modifici căutarea sau filtrele aplicate.
                    </p>
                  </div>
                )}

                {/* ── Pagination (mobile) ── */}
                {totalPages > 1 && (
                  <div className="crm-pagination" style={{ paddingBottom: 80 }}>
                    <button
                      className="crm-pagination__btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                    >
                      <IonIcon icon={chevronBack} style={{ fontSize: 12 }} />
                    </button>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#475569', padding: '0 8px' }}>
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      className="crm-pagination__btn"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      <IonIcon icon={chevronForward} style={{ fontSize: 12 }} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── FAB: add family (admin only) ── */}
        {isAdmin && (
          <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton onClick={() => router.push('/families/new')}>
              <IonIcon icon={add} />
            </IonFabButton>
          </IonFab>
        )}
      </IonContent>
    </IonPage>
  );
};

export default FamilyListPage;
